import { SelectionChangedEvent } from "ag-grid-community";
import { EMPTY_DOC_CHARGE_DATA_CRITERIA } from "presentation/constant/DocumentPreview/DocChargeDataCriteria";
import { DocumentPreviewConstant } from "presentation/constant/DocumentPreview/DocumentPreviewConstant";
import { INITIAL_DOCUMENT_PREVIEW_EDIT_COL_DEF } from "presentation/constant/DocumentPreview/DocumentPreviewEditColumnDefinition";
import { EMPTY_DOCUMENT_PREVIEW_EDIT_SEARCH_CRITERIA } from "presentation/constant/DocumentPreview/DocumentPreviewEditSearchCriteria";
import { addRemoveChargeDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useDocumentPreviewVM } from "presentation/hook/DocumentPreview/useDocumentPreviewVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useDocumentPreviewTracked } from "presentation/store/DocumentPreview/DocumentPreviewProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionCross, Sidebarheader, SidebarTitle, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";
import { DocumentPreviewEditHeaderPanel } from "../EditModal/DocumentPreviewEditHeaderPanel";
import _ from "lodash";


const DocumentPreviewAdjCntrPanel = () => {
    const documentPreviewVM = useDocumentPreviewVM();
    const [documentPreviewState] = useDocumentPreviewTracked();
    const { editSearchCriteria, docChargeDatas, selectDocChargeDataRows, currentSelectedData } = documentPreviewState;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const messageBarVM = useMessageBarVM();


    const handleReset = useCallback(async () => {
        documentPreviewVM.resetEditSearchCriteria();
    }, [documentPreviewVM]);

    const handleAdd = useCallback(async () => {
        if (selectDocChargeDataRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (currentSelectedData?.draftState === "DE") {
            messageBarVM.showError("The draft invoice was submitted ,please do reject first.");
            return;
        }
        if (selectDocChargeDataRows?.some(ety => ety.adreState === "AD")) {
            messageBarVM.showError("Please select record(s) with status is empty.");
            return;
        }
        if (selectDocChargeDataRows?.some(ety => ety.dtlState !== "R" && ety.dtlState !== "F")) {
            messageBarVM.showError("Please select record(s) with state is 'R' or 'F'.");
            return;
        }
        const addRows = selectDocChargeDataRows.filter(ety => ety.draftDocNo === null && ety.adreState !== "AD");
        const updatedRmRows = addRows.map(ety => ({
            ...ety,
            adreState: 'AD',
            updated: !ety.updated
        }));
        documentPreviewVM.updateDocChargeDataRows(updatedRmRows);
    }, [currentSelectedData?.draftState, documentPreviewVM, messageBarVM, selectDocChargeDataRows]);

    const handleRemove = useCallback(async () => {
        if (selectDocChargeDataRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (currentSelectedData?.draftState === "DE") {
            messageBarVM.showError("The draft invoice was submitted ,please do reject first.");
            return;
        }
        if (selectDocChargeDataRows?.some(ety => ety.adreState === "RE")) {
            messageBarVM.showError("Please select record(s) with status is empty.");
            return;
        }
        const rmRows = selectDocChargeDataRows.filter(ety => ety.draftDocNo !== null && ety.draftDocNo === currentSelectedData.draftDocNo && ety.adreState !== "RE");
        const updatedRmRows = rmRows.map(ety => ({
            ...ety,
            adreState: 'RE',
            updated: !ety.updated,
        }));
        documentPreviewVM.updateDocChargeDataRows(updatedRmRows);
    }, [currentSelectedData.draftDocNo, currentSelectedData?.draftState, documentPreviewVM, messageBarVM, selectDocChargeDataRows]);

    const handleClear = useCallback(async () => {
        if (currentSelectedData?.draftState === "DE") {
            messageBarVM.showError("The draft invoice was submitted ,please do reject first.");
            return;
        }
        const clearRows = (selectDocChargeDataRows.length === 0 ? docChargeDatas : selectDocChargeDataRows).filter(ety => ety.adreState !== null || ety.adreState !== '');
        const updatedRows = clearRows.map(ety => ({
            ...ety,
            adreState: '',
            updated: !ety.updated,
        }));

        documentPreviewVM.updateDocChargeDataRows(updatedRows);
    }, [currentSelectedData?.draftState, docChargeDatas, documentPreviewVM, messageBarVM, selectDocChargeDataRows]);

    const handleSubmit = useCallback(async () => {
        if (docChargeDatas.every(ety => ety.updated === false)) {
            messageBarVM.showError("No modified data, no need to submit.");
            return;
        }
        let criteria = {
            ...EMPTY_DOC_CHARGE_DATA_CRITERIA
        }
        criteria = {
            ...EMPTY_DOC_CHARGE_DATA_CRITERIA,
            docChargeDatas: docChargeDatas,
            docHdrId: Number(currentSelectedData.id)
        }
        setIsLoading(true);
        documentPreviewVM.submitDocChargeData(criteria).then((data) => {
            if (data && data.success) {
                documentPreviewVM.searchDocChargeData(currentSelectedData, editSearchCriteria).then((data) => {
                    messageBarVM.showSuccess("Submit success!", true);
                });
            } else {
                messageBarVM.showError(data.data ?? "Submit failed!");
            }
        }).finally(() => {
            setIsLoading(false);
        });
    }, [currentSelectedData, docChargeDatas, documentPreviewVM, editSearchCriteria, messageBarVM]);

    const handleApprove = useCallback(async () => {
        if (docChargeDatas?.some(ety => ety.updated === true)) {
            messageBarVM.showError("Modified data has not been saved,please do submit first.");
            return;
        }
        let editSearchCriteria = { ...EMPTY_DOCUMENT_PREVIEW_EDIT_SEARCH_CRITERIA };
        editSearchCriteria = {
            ...editSearchCriteria,
            draftDocNo: currentSelectedData?.draftDocNo as string ?? null,
            isApprove: true,
        }
        documentPreviewVM.checkAddRemoveCharge(editSearchCriteria, "APPROVE").then(data => {
            if (!data) {
                messageBarVM.showError("No add/remove record(s) found, can not allow to approve.");
                return;
            }
        });
    }, [currentSelectedData?.draftDocNo, docChargeDatas, documentPreviewVM, messageBarVM]);

    const handleReject = useCallback(async () => {
        if (docChargeDatas?.some(ety => ety.updated === true)) {
            messageBarVM.showError("Modified data has not been saved,please do submit first.");
            return;
        }
        let editSearchCriteria = { ...EMPTY_DOCUMENT_PREVIEW_EDIT_SEARCH_CRITERIA };
        editSearchCriteria = {
            ...editSearchCriteria,
            etd: currentSelectedData?.etd as Date ?? null,
            consortiumCode: currentSelectedData?.consortiumCode as string ?? null,
            vesselCode: currentSelectedData?.vesselCode as string ?? null,
            voyageCode: currentSelectedData?.voyageCode as string ?? null,
            billToCompany: currentSelectedData?.billToCompany as string ?? null,
            isApprove: false,
            draftDocNo: currentSelectedData?.draftDocNo as string ?? null,
        }
        documentPreviewVM.checkAddRemoveCharge(editSearchCriteria, "REJECT").then(data => {
            if (!data) {
                messageBarVM.showError("No add/remove record(s) found, can not allow to reject.");
                return;
            }
        });
    }, [currentSelectedData?.billToCompany, currentSelectedData?.consortiumCode, currentSelectedData?.etd, currentSelectedData?.vesselCode, currentSelectedData?.voyageCode, currentSelectedData?.draftDocNo, docChargeDatas, documentPreviewVM, messageBarVM]);

    const handleSearch = useCallback(async () => {
        setIsLoading(true);
        documentPreviewVM.searchDocChargeData(currentSelectedData, editSearchCriteria).then((data) => {
            setIsLoading(false);
        })
    }, [currentSelectedData, documentPreviewVM, editSearchCriteria]);

    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        documentPreviewVM.updateSelectedDocChargeDataRows(selectedRows);
    }, [documentPreviewVM])

    const memoSubChgTypeOptions = useMemo(() => {
        if (!editSearchCriteria.chargeType || editSearchCriteria.chargeType.length === 0) {
            const allCommonSubChgTypeOptions = Object.values(documentPreviewState.dynamicOptions.subChargeTypeDropdownOptions).flat();
            return _.orderBy(_.uniqBy(_.flatMap(_.concat(allCommonSubChgTypeOptions ?? [])), 'value'), 'dropdownLabel');
        }
        const subChgTypeOptions = editSearchCriteria.chargeType ? documentPreviewState.dynamicOptions.subChargeTypeDropdownOptions[editSearchCriteria.chargeType] : [];
        return subChgTypeOptions;
    }, [editSearchCriteria.chargeType, documentPreviewState.dynamicOptions.subChargeTypeDropdownOptions])


    const memoChargeType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "230px", marginBottom: "15px", marginTop: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={editSearchCriteria?.chargeType || ''}
                fieldValue={editSearchCriteria?.chargeType}
                fieldLabel={DocumentPreviewConstant.EditTable.CHARGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeType'}
                maxLength={60}
                options={documentPreviewState.dynamicOptions.chargeTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    documentPreviewVM.onEditSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [editSearchCriteria?.chargeType, documentPreviewState.dynamicOptions.chargeTypeDropdownOptions, documentPreviewVM])

    const memoSubChargeType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "230px", marginBottom: "15px", marginTop: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={editSearchCriteria?.subChargeType || ''}
                fieldValue={editSearchCriteria?.subChargeType}
                fieldLabel={DocumentPreviewConstant.EditTable.SUB_CHARGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'subChargeType'}
                maxLength={60}
                options={memoSubChgTypeOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    documentPreviewVM.onEditSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [documentPreviewVM, editSearchCriteria?.subChargeType, memoSubChgTypeOptions])


    const memoStatus = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "230px", marginBottom: "15px", marginTop: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={editSearchCriteria?.adreState || ''}
                fieldValue={editSearchCriteria?.adreState}
                fieldLabel={DocumentPreviewConstant.EditTable.STATUS}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'adreState'}
                maxLength={60}
                options={addRemoveChargeDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    documentPreviewVM.onEditSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [editSearchCriteria?.adreState, documentPreviewVM])


    const memoDraftDocNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "230px", marginBottom: "15px", marginTop: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={editSearchCriteria?.inputDraftDocNo || ''}
                fieldValue={editSearchCriteria?.inputDraftDocNo}
                fieldLabel={DocumentPreviewConstant.EditTable.DRAFT_DOC_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'inputDraftDocNo'}
                maxLength={60}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    documentPreviewVM.onEditSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [editSearchCriteria?.inputDraftDocNo, documentPreviewVM])

    const memoCntrNoList = useMemo(() =>
        <div className='im-flex-row-item' id="std-proposal-edit-remarks" style={{ width: "520px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                helpTextElement={"Use comma ( , ) as delimiter"}
                readOnlyValue={editSearchCriteria?.cntrNoList.join(',') || ''}
                fieldValue={editSearchCriteria?.cntrNoList}
                fieldLabel={DocumentPreviewConstant.EditTable.CNTR_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXTAREA}
                fieldKey={'cntrNoList'}
                maxLength={200}
                rows={1}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    documentPreviewVM.onEditSearchFieldChange(fieldKey, fieldValue, fFullValue)}
            />
        </div>
        , [editSearchCriteria.cntrNoList, documentPreviewVM])

    const memoDocumentPreviewEditModalTable = useMemo(() => {
        return (
            <>
                <NbisTable
                    id='doc-preview-edit-table'
                    isNewColumnSetting={true}
                    columns={INITIAL_DOCUMENT_PREVIEW_EDIT_COL_DEF?.slice()}
                    data={docChargeDatas}
                    showPaginator={false}
                    editable={false}
                    showAddIcon={false}
                    showDeleteButton={false}
                    showReloadIcon={false}
                    isScrollHighlighted={true}
                    selectionMode={false}
                    isRowHighligted={true}
                    rowSelection={"multiple"}
                    handleSelectionChanged={handleSelectionChange}
                    gridHeight="customHeight"
                    customHeight="calc(100vh - 550px)"
                />
            </>
        );
    }, [docChargeDatas, handleSelectionChange])

    const handleCloseDialog = () => {
        documentPreviewVM.cancelEditModal();
    }

    return <>
        <div className='side-form-content-wrapper' style={{ padding: '15px' }}>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle></SidebarTitle>
                        <SidebarActionCross>
                            <IconButton fileName='Icon-cross' size="medium" onClick={handleCloseDialog} tooltipDisable={true} />
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>
            <DocumentPreviewEditHeaderPanel />
            <TableWrapper>
                <Sidebarheader style={{ width: '100%' }}>
                    <CriteriaItemContainer>
                        {memoDraftDocNo}
                        {memoChargeType}
                        {memoSubChargeType}
                        {memoStatus}
                        {memoCntrNoList}
                    </CriteriaItemContainer>
                </Sidebarheader>
                <StyledAction className="tm-animated-wrapper">
                    {<HPHButton disabled={false} label={WorkspaceConstant.Common.BUTTON_RESET} size={'Small'} theme={'Secondary'} onClick={handleReset} />}
                    {<HPHButton disabled={false} label={WorkspaceConstant.Common.BUTTON_SEARCH} size={'Small'} theme={'Primary'} onClick={handleSearch} />}
                </StyledAction>
                {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
                {memoDocumentPreviewEditModalTable}
            </TableWrapper>
            <StyledAction className="tm-animated-wrapper">
                {<HPHButton disabled={false} label={WorkspaceConstant.Common.BUTTON_ADD} size={'Small'} theme={'Secondary'} onClick={handleAdd} />}
                {<HPHButton disabled={false} label={WorkspaceConstant.Common.BUTTON_REMOVE} size={'Small'} theme={'Secondary'} onClick={handleRemove} />}
                {<HPHButton disabled={false} label={WorkspaceConstant.Common.BUTTON_CLEAR} size={'Small'} theme={'Secondary'} onClick={handleClear} />}
                {<HPHButton disabled={false} label={WorkspaceConstant.Common.BUTTON_SUBMIT} size={'Small'} theme={'Secondary'} onClick={handleSubmit} />}
                {<HPHButton disabled={false} label={WorkspaceConstant.Common.BUTTON_APPROVE} size={'Small'} theme={'Secondary'} onClick={handleApprove} />}
                {<HPHButton disabled={false} label={WorkspaceConstant.Common.BUTTON_REJECT} size={'Small'} theme={'Secondary'} onClick={handleReject} />}
            </StyledAction>
            {documentPreviewState.editForceRefresh && <></>}
        </div>
    </>;
}

export default memo(DocumentPreviewAdjCntrPanel);